/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import { AelEvents, ApplicationView, getAelEventsArray } from '@gh/builder-shared-data';
import { createDefaultGridModel } from '@gh/common-internal-shared';
import {
  ActionFlow,
  getRouteInitData,
  isEntityInvalid,
  MutablePageState,
  TextDictionary,
  TextTypes,
} from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import {
  DialogService,
  FormMode,
  isEditableFormMode,
  NotificationService,
  STYLES_FLEX_COLUMN_LAYOUT,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  buildEditableTextColumn,
  FocusedCell,
  gridCollectionComparator,
  GridDefinition,
  GridDefinitionFactory,
} from '@gh/core-ui-grid';
import { toString, valueComparator } from '@gh/core-util';
import { filter$ } from '@gh/rx/operators';
import { assign, find, fromPairs } from 'lodash';
import { EmailRecipientListBusinessService } from '../resources/email-recipient-list-business.service';
import {
  EmailRecipient,
  emailRecipientArr,
  emailRecipientFieldState,
  EmailRecipientList,
  emailRecipientValidateField,
  getEmailRecipientId,
  hasEmailRecipientsError,
  setEmailRecipientText,
} from '../resources/email-recipients.model';

@Component({
  selector: 'gh-email-recipient-list',
  template: require('./email-recipient-list.component.html'),
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
})
export class EmailRecipientListComponent extends AbstractFormComponent<EmailRecipientList> implements OnInit {
  emailScheduledTaskDict: TextDictionary;
  definition: GridDefinition;
  form: MutablePageState<EmailRecipientList>;

  invalidFocusedCell?: FocusedCell;

  private original: EmailRecipient[];

  constructor(private route: ActivatedRoute,
              private gridDefinitionFactory: GridDefinitionFactory,
              private businessService: EmailRecipientListBusinessService,
              private notifications: NotificationService,
              private translationService: TranslationService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private dialogService: DialogService,
              private navigationService: BuilderNavigationService,
              private userContext: BuilderUserContext) {
    super();
  }

  get editable(): boolean {
    return isEditableFormMode(this.form.mode);
  }

  ngOnInit(): void {
    const { recipients, emailScheduledTaskDict } = getRouteInitData(this.route);
    assign(this, { emailScheduledTaskDict });

    this.form = new MutablePageState(FormMode.EditMode);
    this.form.setValue(recipients);
    this.original = recipients;
    this.definition = this.buildGridDefinition();
  }

  get completeFlow(): ActionFlow<FormFlowState<EmailRecipientList>> {
    return this.pipelines.complete({
      successMessage: 'msg_email_recipients_successfully',
      validate: this.commonActions.validateWithEffect(
        (state) => hasEmailRecipientsError(state.value),
        this.handleError.bind(this)),
      execute: this.commonActions.execute((state) => this.businessService.save(this.form.value)),
      finish: (state: FormFlowState<EmailRecipient[]>) => {
        this.form.setValue(state.value);
        this.original = state.value;
        this.form.resetDirty();
      },
    });
  }

  onReset(): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.form.setValue(this.original);
        this.form.resetDirty();
      });
  }

  goToAel(): void {
    const masterZoneId = this.userContext.masterZone && this.userContext.masterZone.id;

    this.navigationService.goToAel({
      entityId: masterZoneId || void 0,
      entityType: masterZoneId ? 'MASTER_ZONE' : '',
      label: 'lbl_email_recipients',
      eventTypeIds: getAelEventsArray(AelEvents.EmailRecipients),
    });
  }

  onResetFocusedCell(): void {
    this.invalidFocusedCell = void 0;
  }

  private setRecipientValue(recipient: EmailRecipient, value: string): void {
    const newRecipients = emailRecipientArr.merge(
      this.form.value,
      getEmailRecipientId(recipient),
      setEmailRecipientText(recipient, 'recipients', value));

    this.form.setValue(newRecipients);
    this.form.setDirty();
  }

  private buildGridDefinition(): GridDefinition {
    // index necessary for sorting: type ID -> index
    const typeToIndex = fromPairs(this.emailScheduledTaskDict.seq().map(({ id }, i) => [id, i]));
    const model = createDefaultGridModel({
      editable: true,
      getId: getEmailRecipientId,
      getCellDef: emailRecipientFieldState,
      validate: emailRecipientValidateField,
      command: (entity: EmailRecipient, _, value) => this.setRecipientValue(entity, value),
    });
    return this.gridDefinitionFactory.buildManualWithModel({
      model,
      columns: [{
        field: 'typeId',
        sort: 'asc',
        headerName: this.translationService.get('lbl_task_name'),
        suppressSizeToFit: true,
        width: 350,
        comparator: gridCollectionComparator(valueComparator((recipient) => typeToIndex[recipient.typeId])),
        valueGetter: ({ data }: { data: EmailRecipient }) => this.emailScheduledTaskDict.get(toString(data.typeId)),
      }, {
        field: 'recipients',
        headerName: this.translationService.get('lbl_email_recipients'),
        ...buildEditableTextColumn(TextTypes.Unlimited, model.getCellAccessor('recipients')),
      }],
    });
  }

  private handleError(): void {
    const invalid = find<EmailRecipient>(this.form.value, isEntityInvalid);

    if (invalid) {
      this.notifications.error('msg_he_incorrect_data_in_tables');
      this.invalidFocusedCell = { id: getEmailRecipientId(invalid), field: 'recipients' };
    }
  }
}
